import React, { lazy, Suspense, useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import MainLayout from "./layouts/MainLayout";
import { AuthProvider } from "./providers/AuthContext";
import { ToastContainer } from "react-toastify";
import Loader from "./components/Loader/Loader";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import AdminProtectedRoute from "./components/ProtectedRoute/AdminProtectedRoute";
import CookieConsent from "./components/CookieConsent/CookieConsent";
import { initGA } from "./utils/analytics";

// Eager load critical pages
import HomePage from "./pages/Home/HomePage";
import SignInPage from "./pages/Registration/SignIn/SignInPage";
import NotFoundPage from "./pages/NotFound/NotFoundPage";

// Lazy load secondary pages for better performance
const RegistrationPage = lazy(
  () => import("./pages/Registration/RegistrationPage"),
);
const ProfilePage = lazy(() => import("./pages/Profile/ProfilePage"));
const AuctionsPage = lazy(() => import("./pages/Auctions/AuctionsPage"));
const FAQPage = lazy(() => import("./pages/FAQ/FAQ"));
const SupportTicketForm = lazy(
  () => import("./pages/SupportTicketForm/SupportTicketForm"),
);
const MovingCalculatorPage = lazy(
  () => import("./pages/MovingCalclulator/MovingCalculator"),
);
const CreateOrderPage = lazy(
  () => import("./pages/CreateOrder/CreateOrderPage"),
);
const ForgotPasswordPage = lazy(
  () => import("./pages/Registration/ForgotPassword/ForgotPasswordPage"),
);
const ResetPasswordPage = lazy(
  () => import("./pages/Registration/ResetPassword/ResetPasswordPage"),
);
const BillingSuccess = lazy(() => import("./pages/Billing/BillingSuccess"));
const BillingCancelled = lazy(() => import("./pages/Billing/BillingCancelled"));
const RegisterSuccess = lazy(
  () => import("./pages/Registration/RegisterSuccess"),
);
const TermsPage = lazy(() => import("./pages/Legal/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/Legal/PrivacyPage"));
const CookiePage = lazy(() => import("./pages/Legal/CookiePage"));
const AccessibilityPage = lazy(() => import("./pages/Legal/AccessibilityPage"));
const AboutPage = lazy(() => import("./pages/Company/AboutPage"));
const TipsPage = lazy(() => import("./pages/Company/TipsPage"));
const VerifyEmailPage = lazy(
  () => import("./pages/Registration/VerifyEmail/VerifyEmailPage"),
);

// Admin pages
const AdminDashboard = lazy(() => import("./pages/Admin/AdminDashboard"));
const AdminUsersPage = lazy(() => import("./pages/Admin/AdminUsersPage"));
const AdminAuctionsPage = lazy(() => import("./pages/Admin/AdminAuctionsPage"));
const AdminFinancialPage = lazy(() => import("./pages/Admin/AdminFinancialPage"));
const AdminTestDataPage = lazy(() => import("./pages/Admin/AdminTestDataPage"));
const CompanyVerificationPage = lazy(() => import("./pages/Admin/CompanyVerificationPage"));

const App = () => {
  // Initialize Google Analytics on app load
  useEffect(() => {
    initGA();
  }, []);

  return (
    <>
      <AuthProvider>
        <ScrollToTop />
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route path="/" element={<MainLayout />}>
              <Route index element={<HomePage />} />
              <Route path="register" element={<RegistrationPage />} />
              <Route path="signin" element={<SignInPage />} />
              <Route path="forgot-password" element={<ForgotPasswordPage />} />
              <Route path="reset-password" element={<ResetPasswordPage />} />
              <Route path="verify-email" element={<VerifyEmailPage />} />

              <Route
                path="profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="auctions"
                element={
                  <ProtectedRoute>
                    <AuctionsPage />
                  </ProtectedRoute>
                }
              />
              <Route path="faq" element={<FAQPage />} />
              <Route path="support/ticket" element={<SupportTicketForm />} />
              <Route path="calculator" element={<MovingCalculatorPage />} />
              <Route path="create-order" element={<CreateOrderPage />} />

              <Route path="billing/success" element={<BillingSuccess />} />
              <Route path="billing/cancelled" element={<BillingCancelled />} />
              <Route path="register/success" element={<RegisterSuccess />} />

              <Route path="terms" element={<TermsPage />} />
              <Route path="privacy" element={<PrivacyPage />} />
              <Route path="cookies" element={<CookiePage />} />
              <Route path="accessibility" element={<AccessibilityPage />} />

              <Route path="about" element={<AboutPage />} />
              <Route path="tips" element={<TipsPage />} />

              {/* Admin Routes */}
              <Route
                path="admin/dashboard"
                element={
                  <AdminProtectedRoute>
                    <AdminDashboard />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="admin/users"
                element={
                  <AdminProtectedRoute>
                    <AdminUsersPage />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="admin/auctions"
                element={
                  <AdminProtectedRoute>
                    <AdminAuctionsPage />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="admin/financial"
                element={
                  <AdminProtectedRoute>
                    <AdminFinancialPage />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="admin/test-data"
                element={
                  <AdminProtectedRoute>
                    <AdminTestDataPage />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="admin/company-verification"
                element={
                  <AdminProtectedRoute>
                    <CompanyVerificationPage />
                  </AdminProtectedRoute>
                }
              />

              {/* 404 Page */}
              <Route path="404" element={<NotFoundPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </Suspense>
      </AuthProvider>
      <CookieConsent />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
};

export default App;
